import { Event } from "../../models/events";
import {
  EventAttendanceConfig,
  SaveEventAttendanceConfigPayload,
} from "../../models/attendance";

/**
 * Builds the body that duplicating an event posts to `POST /events`.
 *
 * There is no duplicate endpoint: the backoffice copies the original and sends
 * it as a brand new event, so every field the copy gets wrong ships as a real
 * event someone has to fix by hand. Extracted from DuplicateEventForm so the
 * mapping can be tested without rendering the dialog.
 *
 * Note that the host is NOT part of this: it travels in a second request
 * (`updateEventHost`) after the event exists, with no transaction around the
 * pair, so a failure there leaves the duplicate hostless.
 */
export const buildDuplicateEventPayload = (
  event: Event,
  startDate: Date,
  suppressionAllowed: boolean
) => ({
  ...event,
  importantMessage: event.importantMessage,
  active: !!event.active,
  paymentType: event.paymentType || "FREE",
  startDate: startDate.toISOString(),
  hostAssists: !!event.hostAssists,
  // Both flags are silently dropped when the advanced config isn't visible for
  // this event's tenant/type, so a copy can't carry over a suppression the
  // operator isn't allowed to set.
  suppressNotifications: suppressionAllowed && !!event.suppressNotifications,
  suppressRatingPrompt: suppressionAllowed && !!event.suppressRatingPrompt,
});

/**
 * Lo que la copia manda a `PUT /events/:id/attendance-config`, o `null` si no hay nada
 * que copiar.
 *
 * La asistencia no viaja en el body del evento: vive en su propio recurso, así que
 * duplicar un evento que toma asistencia dejaba la copia sin la sección prendida y
 * nadie podía registrarla desde la app. Un config apagado es equivalente a no tener
 * config, así que sólo se copia el que está prendido.
 *
 * `attendanceHostUserId` sólo se copia si esa persona va a ser host del duplicado: el
 * worker exige que el usuario esté inscripto como host (`is_host`) del evento, fila que
 * `PATCH /bo/events/host` crea únicamente cuando el host participa. Si no se cumple va
 * `null` — el default "mismo host del evento", que el backend resuelve — en vez de
 * perder la toma de asistencia entera por un host inválido.
 */
export const buildDuplicatedAttendanceConfigPayload = (
  config: EventAttendanceConfig | null | undefined,
  event: Pick<Event, "hostId" | "hostAssists">,
  newEventId: number,
  hostAssigned: boolean
): SaveEventAttendanceConfigPayload | null => {
  if (!config?.enabled) return null;

  const keepsAttendanceHost =
    config.attendanceHostUserId !== null &&
    hostAssigned &&
    !!event.hostAssists &&
    config.attendanceHostUserId === event.hostId;

  return {
    eventId: newEventId,
    enabled: true,
    method: config.method,
    attendanceHostUserId: keepsAttendanceHost ? config.attendanceHostUserId : null,
    maxAttendees: config.maxAttendees,
    windowStartOffsetMin: config.windowStartOffsetMin,
    windowEndHours: config.windowEndHours,
  };
};

/** La copia quedó tomando asistencia, pero con el host del evento en vez del original. */
export const DUPLICATED_EVENT_ATTENDANCE_HOST_RESET_NOTICE =
  "La copia toma asistencia con el host del evento: quien la tomaba en el original no quedó asignado. Podés cambiarlo editando la copia.";

/** El evento se creó, pero la configuración de asistencia no se pudo copiar. */
export const DUPLICATED_EVENT_ATTENDANCE_FAILED_NOTICE =
  "No se pudo copiar la configuración de asistencia: la copia quedó sin tomar asistencia.";

/** No se pudo leer la configuración del original, así que no se sabe si había que copiarla. */
export const DUPLICATED_EVENT_ATTENDANCE_UNKNOWN_NOTICE =
  "No se pudo leer la configuración de asistencia del original: revisá la sección Asistencia en la copia.";
